"use client";

/**
 * Folder dropdown selector.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Combobox } from "@/components/shared/combobox";
import { useFolderDetail, useFolderList } from "../queries";
import { useFolderCreateAction } from "./use-folder-create-action";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { cn } from "@/lib/utils/cn";

// Pass-through filter: search is handled server-side, so the Combobox must not
// re-filter the results it's given.
const showAll = () => true;

// -- Types -----------------------------------------------------------

interface FolderSelectProps {
  value?: string;
  onChange: (folderId: string) => void;
  placeholder?: string;
  className?: string;
  excludeIds?: string[];
  disabled?: boolean;
  /** Pins a clearable "No folder" entry (value "") at the top of the options. */
  emptyOption?: { label: string };
  /** Forwarded to the underlying Combobox — validation error state/message. */
  error?: boolean | string;
  /**
   * Owns the create flow: renders the "+ Create Folder" topAction and hosts
   * a quick-create FolderDialog internally, auto-selecting the new folder.
   * Takes precedence over `showCreateOption`/`onCreateFolder` if both are set.
   */
  allowCreate?: boolean;
  /** @deprecated Prefer `allowCreate` — kept for existing call sites that host their own FolderDialog. */
  showCreateOption?: boolean;
  /** @deprecated Prefer `allowCreate` — kept for existing call sites that host their own FolderDialog. */
  onCreateFolder?: () => void;
  id?: string;
  "aria-label"?: string;
  "data-testid"?: string;
}

// -- Component -------------------------------------------------------

export function FolderSelect({
  value,
  onChange,
  placeholder,
  className,
  excludeIds = [],
  disabled = false,
  emptyOption,
  error,
  allowCreate = false,
  showCreateOption = false,
  onCreateFolder,
  id,
  "aria-label": ariaLabel,
  "data-testid": testId,
}: FolderSelectProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const folderCreate = useFolderCreateAction({ onCreated: onChange });

  const { data, isLoading, isFetching } = useFolderList({
    pageSize: 200,
    query: debouncedSearch,
  });
  const folders = data?.folders ?? [];

  // The server honours the page cap, so a folder ordered past the first 200 never
  // arrives and the trigger would render the placeholder over a real selection.
  // Fetch that one folder by id instead. Skipped while searching, where the
  // Combobox's own seen-option memory already holds the label.
  const missingSelected =
    !!value &&
    !debouncedSearch &&
    !isLoading &&
    !folders.some((f) => f.folderId === value);
  const { data: selectedFolder } = useFolderDetail(
    missingSelected ? value : undefined,
  );

  const options = [
    ...(emptyOption ? [{ value: "", label: emptyOption.label }] : []),
    ...(selectedFolder && missingSelected && !excludeIds.includes(value!)
      ? [{ value: value!, label: selectedFolder.name }]
      : []),
    ...folders
      .filter((f) => f.folderId && !excludeIds.includes(f.folderId))
      .map((f) => ({
        value: f.folderId!,
        label: f.name,
      })),
  ];

  const topAction = allowCreate
    ? folderCreate.topAction
    : showCreateOption && onCreateFolder
      ? { label: folderCreate.topAction.label, onSelect: onCreateFolder }
      : undefined;

  return (
    <>
      <Combobox
        id={id}
        value={value ?? ""}
        onChange={onChange}
        options={options}
        placeholder={placeholder ?? t("FOLDERS.SELECT_FOLDER_PLACEHOLDER")}
        error={error}
        disabled={disabled}
        loading={isLoading || isFetching}
        onSearchChange={setSearch}
        filter={showAll}
        className={cn("w-full", className)}
        aria-label={ariaLabel}
        data-testid={testId}
        topAction={topAction}
      />
      {allowCreate && folderCreate.panel}
    </>
  );
}
